import {
  attachValidation,
  EditableAccessRule,
  PersistedAccessRule,
  ValidationError,
} from "../../models";
import {
  areTimesEqual,
  getEndOfDay,
  getStartOfDay,
  normalizeToTimeOfDaySeconds,
  parseTimeOrEndOfDay,
  parseTimeOrStartOfDay,
} from "../ruleEditorTimeUtils";

export type DayOfWeek = {
  id: number;
  label: string;
};

export interface BlackoutDayOfWeekIdentity {
  dayOfWeekId: number | null;
  blackoutTimeSlotStart?: Date | string | null;
  blackoutTimeSlotEnd?: Date | string | null;
}

export interface BlackoutDayOfWeekAccessRule extends PersistedAccessRule {
  dayOfWeekId: number;
  blackoutTimeSlotStart?: string;
  blackoutTimeSlotEnd?: string;
}

export interface EditableBlackoutDayOfWeek extends EditableAccessRule {
  groupId?: string;
  dayOfWeekId: number | null;
  isSelected: boolean;
}

export interface EditableBlackoutDayOfWeekAccessRule extends EditableBlackoutDayOfWeek {
  blackoutTimeSlotStart: Date | null;
  blackoutTimeSlotEnd: Date | null;
}

export type EditableBlackoutDayOfWeekRuleGroup = {
  id: string;
  blackoutTimeSlotStart: Date | null;
  blackoutTimeSlotEnd: Date | null;
  rules: EditableBlackoutDayOfWeek[];
  isNew: boolean;
  isDeleted: boolean;
};

export const blackoutDayOfWeekDiffAdapter = {
  isDeleted: (rule: EditableBlackoutDayOfWeekAccessRule) =>
    rule.isSelected === false,

  isUpdated: (
    original: BlackoutDayOfWeekAccessRule,
    current: EditableBlackoutDayOfWeekAccessRule,
  ) =>
    !areTimesEqual(
      original.blackoutTimeSlotStart,
      current.blackoutTimeSlotStart,
    ) ||
    !areTimesEqual(original.blackoutTimeSlotEnd, current.blackoutTimeSlotEnd),

  getIdentityKey: getBlackoutDayOfWeekIdentityKey,
};

function timeKey(date: Date | null): string {
  if (!date) return "null";

  const h = date.getHours().toString().padStart(2, "0");
  const m = date.getMinutes().toString().padStart(2, "0");
  const s = date.getSeconds().toString().padStart(2, "0");

  return `${h}:${m}:${s}`;
}

export const toEditableBlackoutDayOfWeekRuleGroups = (
  rules: BlackoutDayOfWeekAccessRule[],
): EditableBlackoutDayOfWeekRuleGroup[] => {
  const groupsByTimeSlot = new Map<
    string,
    EditableBlackoutDayOfWeekRuleGroup
  >();

  for (const rule of rules) {
    const startKey = timeKey(parseTimeOrStartOfDay(rule.blackoutTimeSlotStart));
    const endKey = timeKey(parseTimeOrEndOfDay(rule.blackoutTimeSlotEnd));
    const slotKey = `${startKey}|${endKey}`;

    let group = groupsByTimeSlot.get(slotKey);
    if (!group) {
      group = {
        id: crypto.randomUUID(),
        blackoutTimeSlotStart: parseTimeOrStartOfDay(
          rule.blackoutTimeSlotStart,
        ),
        blackoutTimeSlotEnd: parseTimeOrEndOfDay(rule.blackoutTimeSlotEnd),
        rules: [],
        isNew: false,
        isDeleted: false,
      };
      groupsByTimeSlot.set(slotKey, group);
    }

    const editableRule = {
      ...toEditableBlackoutDayOfWeekAccessRule(rule),
      groupId: group.id,
    };

    group.rules.push(editableRule);
  }

  return Array.from(groupsByTimeSlot.values());
};

export const toEditableBlackoutDayOfWeekAccessRule = (
  rule: BlackoutDayOfWeekAccessRule,
): EditableBlackoutDayOfWeekAccessRule =>
  attachValidation<EditableBlackoutDayOfWeekAccessRule>(
    {
      ...rule,
      localRuleId: crypto.randomUUID(),
      isNew: false,
      blackoutTimeSlotStart: parseTimeOrStartOfDay(rule.blackoutTimeSlotStart),
      blackoutTimeSlotEnd: parseTimeOrEndOfDay(rule.blackoutTimeSlotEnd),
      isSelected: true,
    },
    validate,
  );

export const createEmptyEditableBlackoutDayOfWeek = (
  overrides?: Partial<EditableBlackoutDayOfWeek>,
): EditableBlackoutDayOfWeek =>
  attachValidation<EditableBlackoutDayOfWeek>(
    {
      localRuleId: crypto.randomUUID(),
      isNew: true,
      liftAccessRuleId: null,
      dayOfWeekId: null,
      isSelected: false,
      ...overrides,
    },
    // Reads the time slot off `this`, so don't swap it for a closure.
    validate,
  );

export const createEmptyBlackoutDayOfWeekRuleGroup = (
  overrides?: Partial<EditableBlackoutDayOfWeekRuleGroup>,
): EditableBlackoutDayOfWeekRuleGroup => {
  const groupId = crypto.randomUUID();

  return {
    id: groupId,
    isNew: true,
    isDeleted: false,
    blackoutTimeSlotStart: getStartOfDay(),
    blackoutTimeSlotEnd: getEndOfDay(),
    rules: overrides?.rules ?? [],

    ...overrides,
  };
};

export function createBlackoutDayOfWeekFromGroup(
  groupId: string,
  overrides?: Partial<EditableBlackoutDayOfWeek>,
): EditableBlackoutDayOfWeek {
  const template = createEmptyEditableBlackoutDayOfWeek();

  return attachValidation<EditableBlackoutDayOfWeek>(
    {
      ...template,
      groupId: groupId,
      isSelected: true,
      ...overrides,
    },
    validate,
  );
}

export function groupEditableRulesByGroupId(
  rules: EditableBlackoutDayOfWeekAccessRule[],
  existingGroups: EditableBlackoutDayOfWeekRuleGroup[] = [],
): EditableBlackoutDayOfWeekRuleGroup[] {
  const groupsById = new Map<string, EditableBlackoutDayOfWeekRuleGroup>();

  // Seed existing groups (including empty ones)
  for (const group of existingGroups) {
    groupsById.set(group.id, {
      ...group,
      rules: [],
    });
  }

  for (const rule of rules) {
    if (!rule.groupId) {
      continue;
    }

    let group = groupsById.get(rule.groupId);

    if (!group) {
      group = {
        id: rule.groupId,
        isNew: true,
        isDeleted: false,
        blackoutTimeSlotStart: rule.blackoutTimeSlotStart ?? null,
        blackoutTimeSlotEnd: rule.blackoutTimeSlotEnd ?? null,
        rules: [],
      };
      groupsById.set(group.id, group);
    }

    group.rules.push(rule);
  }

  // Return all groups (including empty ones)
  return Array.from(groupsById.values());
}

/**
 * Structural parameter because this is also attached to group members, which carry no
 * time slot until useAccessRulesEditor grafts the group's one on.
 */
export function validate(rule: {
  dayOfWeekId: number | null;
  blackoutTimeSlotStart?: Date | null;
  blackoutTimeSlotEnd?: Date | null;
}): ValidationError[] {
  const errors: ValidationError[] = [];

  if (
    rule.dayOfWeekId == null ||
    rule.blackoutTimeSlotStart == null ||
    rule.blackoutTimeSlotEnd == null
  ) {
    errors.push({
      messageKey: "ACCESS_RULE_VALIDATION_BLACKOUT_DOW_REQUIRED_FIELD_MISSING",
    });
  } else if (
    rule.blackoutTimeSlotStart != null &&
    rule.blackoutTimeSlotEnd != null &&
    rule.blackoutTimeSlotStart > rule.blackoutTimeSlotEnd
  ) {
    errors.push({
      messageKey: "ACCESS_RULE_VALIDATION_BLACKOUT_DOW_TIME_START_BEFORE_END",
    });
  }

  return errors;
}

/**
 * Identity key for blackout day of week rules
 * de-duplication
 * - Ignores IDs and lifecycle flags
 * - Time slots are normalized to HH:mm:ss
 */
function getBlackoutDayOfWeekIdentityKey(
  rule: BlackoutDayOfWeekIdentity,
): string {
  return [
    rule.dayOfWeekId,
    normalizeToTimeOfDaySeconds(rule.blackoutTimeSlotStart),
    normalizeToTimeOfDaySeconds(rule.blackoutTimeSlotEnd),
  ].join("|");
}
